// @ts-ignore - JS module
import { WSClient } from '../../../../goServerAPI/apiBase/wsClient.js';
import { clearAutoDisconnectTimer } from './autoDisconnect';

export const initialSalesCallStatus = {
  isActive: false,
  isPaused: false,
  pauseStartedAt: null,
  accumulatedPauseMs: 0,
  callMode: 'real' as 'real' | 'simulator',
  simulatorDifficulty: 'medium' as 'easy' | 'medium' | 'hard',
  simulatorVoice: 'Rex' as 'Eve' | 'Ara' | 'Rex' | 'Sal' | 'Leo',
  simulatorStatus: null,
  callId: null,
  salesmanMicId: null,
  clientMicId: null,
  startTime: null,
  accumulatedSummary: {
    messages: [],
    ammoItems: [],
    lastProcessedSummaryRef: '',
    lastProcessedAmmoRef: '',
    summaryMessageIdCounter: 0,
    ammoIdCounter: 0,
    feedbackItems: [],
    feedbackIdCounter: 0,
  },
  agentConnections: {
    isResponseNeeded: false,
    salesStage: false,
    speechTone: false,
    summarizer: false,
    whatToResponse: false,
  },
  agentData: {
    isResponseNeeded: null,
    salesStage: null,
    speechTone: null,
    summarizer: null,
    whatToResponse: null,
    whatToResponseHistory: [],
    fullTranscriptResult: null,
    shortSummaryResult: null,
    feedbackResult: null,
  },
  lastHelpInteractionTime: undefined,
  manualWhatToSayLock: false,
};

export const createSetters = (set: any, _get: any) => ({
  setCallMode: (mode: 'real' | 'simulator') => {
    set((state: any) => ({
      salesCallStatus: {
        ...state.salesCallStatus,
        callMode: mode,
      },
    }));
  },

  setSimulatorDifficulty: (difficulty: 'easy' | 'medium' | 'hard') => {
    set((state: any) => ({
      salesCallStatus: {
        ...state.salesCallStatus,
        simulatorDifficulty: difficulty,
      },
    }));
  },

  setSimulatorVoice: (voice: 'Eve' | 'Ara' | 'Rex' | 'Sal' | 'Leo') => {
    set((state: any) => ({
      salesCallStatus: {
        ...state.salesCallStatus,
        simulatorVoice: voice,
      },
    }));
  },

  resetConversationData: (preserveFeedback = false) => {
    console.log(`[SALES] Resetting conversation data (preserveFeedback=${preserveFeedback})...`);
    clearAutoDisconnectTimer();

    try {
      WSClient.closeAll();
    } catch (error) {
      console.warn('[SALES] Error closing WebSocket connections during reset:', error);
    }

    set((state: any) => ({
      salesCallStatus: {
        ...state.salesCallStatus,
        isPaused: false,
        pauseStartedAt: null,
        accumulatedPauseMs: preserveFeedback ? state.salesCallStatus.accumulatedPauseMs : 0,
        simulatorStatus: null,
        accumulatedSummary: {
          messages: [],
          ammoItems: [],
          lastProcessedSummaryRef: '',
          lastProcessedAmmoRef: '',
          summaryMessageIdCounter: 0,
          ammoIdCounter: 0,
          feedbackItems: [],
          feedbackIdCounter: 0,
        },
        agentData: {
          isResponseNeeded: null,
          salesStage: null,
          speechTone: null,
          summarizer: null,
          whatToResponse: null,
          whatToResponseHistory: [],
          fullTranscriptResult: null,
          shortSummaryResult: null,
          summaryResult: preserveFeedback ? (state.salesCallStatus.agentData?.summaryResult || null) : null,
          whatsappResult: preserveFeedback ? (state.salesCallStatus.agentData?.whatsappResult || null) : null,
          analysisResult: preserveFeedback ? (state.salesCallStatus.agentData?.analysisResult || null) : null,
          callDurationSeconds: preserveFeedback ? (state.salesCallStatus.agentData?.callDurationSeconds ?? null) : null,
          isSummaryStreaming: false,
          isWhatsappStreaming: false,
          isAnalysisStreaming: false,
          isAnalysisRequested: false,
          ...(preserveFeedback ? {
            feedbackResult: state.salesCallStatus.agentData?.feedbackResult,
            isFeedbackStreaming: false,
            feedbackCallId: state.salesCallStatus.agentData?.feedbackCallId
          } : {
            feedbackResult: null,
            isFeedbackStreaming: false,
            feedbackCallId: null
          })
        },
        lastHelpInteractionTime: undefined,
        startTime: preserveFeedback ? state.salesCallStatus.startTime : null,
      },
      transcriptionStatus: {
        isActive: false,
        connectedDevices: [],
        lastTranscription: null,
        transcriptionHistory: [],
        liveTranscriptions: {},
      },
      salesError: null,
      urgentHelpInFlight: false,
      objectionInFlight: false,
    }));

    console.log('[SALES] Conversation data reset completed');
  },

  setFeedbackStreaming: (isStreaming: boolean) => {
    set((state: any) => ({
      salesCallStatus: {
        ...state.salesCallStatus,
        agentData: {
          ...state.salesCallStatus.agentData,
          isFeedbackStreaming: isStreaming,
        },
      },
    }));
  },

  setFeedbackResult: (result: any) => {
    set((state: any) => ({
      salesCallStatus: {
        ...state.salesCallStatus,
        agentData: {
          ...state.salesCallStatus.agentData,
          feedbackResult: result,
        },
      },
    }));
  },

  setSummaryStreaming: (isStreaming: boolean) => {
    set((state: any) => ({
      salesCallStatus: {
        ...state.salesCallStatus,
        agentData: {
          ...state.salesCallStatus.agentData,
          isSummaryStreaming: isStreaming,
        },
      },
    }));
  },

  setSummaryResult: (result: { short_summary: string } | null) => {
    set((state: any) => ({
      salesCallStatus: {
        ...state.salesCallStatus,
        agentData: {
          ...state.salesCallStatus.agentData,
          summaryResult: result,
        },
      },
    }));
  },

  setWhatsappStreaming: (isStreaming: boolean) => {
    set((state: any) => ({
      salesCallStatus: {
        ...state.salesCallStatus,
        agentData: {
          ...state.salesCallStatus.agentData,
          isWhatsappStreaming: isStreaming,
        },
      },
    }));
  },

  setWhatsappResult: (result: { whatsapp_message: string } | null) => {
    set((state: any) => ({
      salesCallStatus: {
        ...state.salesCallStatus,
        agentData: {
          ...state.salesCallStatus.agentData,
          whatsappResult: result,
        },
      },
    }));
  },

  setAnalysisStreaming: (isStreaming: boolean) => {
    set((state: any) => ({
      salesCallStatus: {
        ...state.salesCallStatus,
        agentData: {
          ...state.salesCallStatus.agentData,
          isAnalysisStreaming: isStreaming,
        },
      },
    }));
  },

  setAnalysisResult: (result: any) => {
    set((state: any) => ({
      salesCallStatus: {
        ...state.salesCallStatus,
        agentData: {
          ...state.salesCallStatus.agentData,
          analysisResult: result,
        },
      },
    }));
  },

  setAnalysisRequested: (requested: boolean) => {
    set((state: any) => ({
      salesCallStatus: {
        ...state.salesCallStatus,
        agentData: {
          ...state.salesCallStatus.agentData,
          isAnalysisRequested: requested,
        },
      },
    }));
  },
});
